import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Checkbox.css';

/**
 * Checkbox Component
 * 
 * An accessible checkbox component with label and validation states.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.checked - Checked state (controlled)
 * @param {boolean} props.defaultChecked - Default checked state (uncontrolled)
 * @param {function} props.onChange - Change handler
 * @param {string} props.label - Label text
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.required - Required field
 * @param {string} props.size - Checkbox size: 'sm' | 'md' | 'lg'
 * @param {string} props.variant - Visual state: 'default' | 'error'
 * @param {string} props.name - Checkbox name attribute
 * @param {string} props.id - Checkbox id attribute
 * @param {string} props.value - Checkbox value attribute
 * @param {string} props.className - Additional CSS classes
 */
const Checkbox = forwardRef(({
  checked,
  defaultChecked,
  onChange,
  label,
  disabled = false,
  required = false,
  size = 'md',
  variant = 'default',
  name,
  id,
  value,
  className = '',
  ...props
}, ref) => {
  const containerClasses = [
    'checkbox-container',
    `checkbox-container--${size}`,
    `checkbox-container--${variant}`,
    disabled ? 'checkbox-container--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const checkboxClasses = [
    'checkbox',
    `checkbox--${size}`
  ].filter(Boolean).join(' ');

  return (
    <label className={containerClasses}>
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={onChange}
        disabled={disabled}
        required={required}
        name={name}
        id={id}
        value={value}
        className={checkboxClasses}
        aria-invalid={variant === 'error' ? 'true' : undefined}
        aria-required={required ? 'true' : undefined}
        {...props}
      />
      
      <span className="checkbox__checkmark" aria-hidden="true">
        <svg
          className="checkbox__icon"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.3333 4L6 11.3333L2.66667 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      
      {label && (
        <span className="checkbox__label">
          {label}
          {required && <span className="checkbox__required" aria-label="required"> *</span>}
        </span>
      )}
    </label>
  );
});

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['default', 'error']),
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  className: PropTypes.string,
};

export default Checkbox;
